/**
 * Code Table Types and Constants
 *
 * Minimal interfaces for code table data - only includes fields actually used by frontend.
 * This allows backend to safely remove unused fields in the future.
 */

/**
 * Minimal interface for code table value items
 * Only includes fields actually used by frontend
 */
export interface CodeValue {
  id: number;
  name: string;
  typeCode?: string; // Used for filtering within values (e.g., 'approved_ref')
  codeRef?: string; // Mapping for Go backend
  displaySequence?: number; // Used for sorting display order
}

/**
 * Minimal interface for code table
 * Only includes fields actually used by frontend
 */
export interface CodeTable {
  typeCode: string; // Primary key for lookup
  values: CodeValue[];
}

/**
 * Dropdown option format - standardized for MUI Select/Autocomplete
 */
export interface DropdownOption {
  value: number;
  label: string;
  typeCode?: string; // For filtering
}

/**
 * All known typeCode values used in the application
 * Add new codes here as they're created
 */
export const CODE_TABLE_TYPES = {
  // User & Authentication
  USER_TYPE: 'user_ref',
  WORK_TYPE: 'worktype_ref',
  EMPLOYMENT_TYPE: 'employment_type_ref',

  // Leave Management
  LEAVE_STATUS: 'leave_status_ref',
  LEAVE_TYPE: 'leave_type_ref',
  COMP_OFF: 'comp_off_ref',

  // Personal Info
  GENDER: 'gender_ref',
  MARITAL_STATUS: 'marital_status_ref',
  BLOOD_GROUP: 'blood_group_ref',
  RELATIONSHIP: 'relationship_ref',
  NATIONALITY: 'nationality_ref',

  // Task/Ticket Management
  STATUS: 'status_ref',
  TASK_STATUS: 'task_status_ref',
  TASK_TYPE: 'tasktype_ref',
  SOURCE: 'source_ref',
  CATEGORY_TYPE: 'categorytype_ref',
  CATEGORY: 'category_ref',
  CATEGORY_TICKETTOOL: 'category_tickettool',
  PRIORITY: 'priority_ref',
  URGENCY: 'urgency_ref',
  IMPACT: 'impact_ref',
  GROUP: 'group_ref',
  AGENT: 'agent_ref',
  COMPANY: 'company_ref',
  TYPE: 'type_ref',

  // Settings & Configuration
  DATE_FORMAT: 'dateformat_ref',
  ORGANISATION_TYPE: 'organisation_type_ref',
  DOCUMENT_TYPE: 'documenttype_ref',
  MEDIA_CATEGORY: 'mediacategory_ref',
  TAX_TYPE: 'taxtype_ref',
  TAX_CATEGORY: 'taxcategory_ref',

  // Defect Tracker
  SEVERITY: 'severity_ref',
  FOUND_AT: 'found_at_ref',
  DEFECT_TYPE: 'defect_type_ref',
  DF_LOGIN_TYPE: 'df_logintype_ref',
  DF_MODULE: 'df_module_ref',
  DF_PRIORITY: 'df_priority_ref',
  DF_TASK_TYPE: 'df_tasktype_ref',
  DF_STATUS: 'df_status_ref',
  DF_BUG_TYPE: 'df_bugtype_ref',
  DF_DEFECT_TYPE: 'df_defecttype_ref',

  // Request Types
  REQUEST: 'request_ref',

  // Time/Scheduling
  TIMEZONE: 'timezones_ref',

  // Ticket-specific
  TAGS: 'tags_ref',
  HANDOVER_REASON: 'handover_reason',

  // Projects
  PROJECT: 'project_ref',

  // Expenses
  EXPENSE_CATEGORY: 'expense_category_ref',

  // Leave Configuration
  ALLOCATION_PATTERN: 'allocation_ref',
  LEAVE_CATEGORY: 'leavecategory_ref',

  // Employee Details
  DOCUMENTS: 'documents_ref',
  MARITAL: 'marital_ref',
  RELATION: 'relation_ref',
  BLOOD: 'blood_ref',
  PROFICIENCY_LEVEL: 'proficiency_level_ref',
  DESIGNATION: 'designation_ref'
} as const;

export type CodeTableType = (typeof CODE_TABLE_TYPES)[keyof typeof CODE_TABLE_TYPES];

/**
 * Common status typeCode values found within code values
 * Used for filtering specific statuses within a code table
 */
export const STATUS_CODES = {
  APPROVED: 'approved_ref',
  DECLINED: 'decline_ref',
  CANCELLED: 'cancel_ref',
  CANCEL_REQUEST: 'cancel_request_ref',
  REQUEST: 'request_ref',
  CLOSED: 'closed_ref',
  PENDING: 'pending_ref',
  AVAILED: 'availed_ref'
} as const;

export type StatusCode = (typeof STATUS_CODES)[keyof typeof STATUS_CODES];
